
import { dynamicImportWithRetry } from "@fatso83/retry-dynamic-import"

import on_domready from "domready"
import log from "plugins/loglevel"

import PageInstance from "app/page_instance/index"
import {
  captureException,
} from "app/error_reporting"

import {
  SimplePerformanceMetric,
} from "plugins/@figliolia/metrics"

import {
  requestIdleCallback,
} from "plugins/request-animation-frame-polyfill"

import readyState from "ready-state"


// Track visibility change
// https://blog.superhuman.com/performance-metrics-for-blazingly-fast-web-apps-ec12efa26bcb
//
// Partially rely on `docEverHidden` from performance_tracking_tags cell
let docEverHidden = (window.docEverHidden != null) ? window.docEverHidden : document.hidden
// Backup event listener
if (window.docEverHidden == null) {
  window.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") { docEverHidden = true }
  })
}


function start() {
  // No `return`/`await`, non blocking
  new SimplePerformanceMetric("es6_onLoad:import").measureAsync(() => {
    return dynamicImportWithRetry(() => import("application/es6_onLoad"))
  }).then((module) => {
    if (module?.default) {
      const es6_onLoad = module.default
      new SimplePerformanceMetric("es6_onLoad:run").measure(es6_onLoad)
    }
  })
  .catch((error) => {
    captureException(error)
  })
  .then(() => {
    on_domready(async () => {
      await new SimplePerformanceMetric("page_start").measureAsync(page_start)
    })
  })
  .catch((error) => {
    captureException(error)
  })

  requestIdleCallback(() => {
    dynamicImportWithRetry(() => import(
      "app/network_latency_to_spacious"
    ))
    .then((module) => {
      if (module?.refreshNetworkLatencyToSpaciousPromise) {
        module.refreshNetworkLatencyToSpaciousPromise()
      }
    })
    .catch((error) => {
      captureException(error)
    })
  })

  requestIdleCallback(() => {
    dynamicImportWithRetry(() => import(
      "app/network_latency_to_spacious_app_server"
    ))
    .then((module) => {
      if (module?.refreshNetworkLatencyToSpaciousAppServerPromise) {
        module.refreshNetworkLatencyToSpaciousAppServerPromise()
      }
    })
    .catch((error) => {
      captureException(error)
    })
  })
}

function page_start() {
  let page_load_promise = Promise.resolve()
  switch(PageInstance.currentPage()){
    case PageInstance.Enum.city_level_landing_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/top_level/city_level_landing_page/index"
      ))
      .then((CityLevelLandingPage) => {
        CityLevelLandingPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.about_us_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/top_level/about_us_page/index"
      ))
      .then((AboutUsPage) => {
        AboutUsPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.city_level_residential_neighbourhood_list_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/cities/city_level_residential_neighbourhood_list_page/index"
      ))
      .then((CityLevelResidentialNeighbourhoodListPage) => {
        CityLevelResidentialNeighbourhoodListPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.city_level_residential_rental_onboarding_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/cities/city_level_residential_rental_onboarding_page/index"
      ))
      .then((CityLevelResidentialRentalOnboardingPage) => {
        CityLevelResidentialRentalOnboardingPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.city_level_residential_sale_onboarding_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/cities/city_level_residential_sale_onboarding_page/index"
      ))
      .then((CityLevelResidentialSaleOnboardingPage) => {
        CityLevelResidentialSaleOnboardingPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.city_level_residential_for_rent_listing_search_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/cities/city_level_residential_for_rent_listing_search_page/index"
      ))
      .then((CityLevelResidentialForRentListingSearchPage) => {
        CityLevelResidentialForRentListingSearchPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.city_level_residential_for_sale_listing_search_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/cities/city_level_residential_for_sale_listing_search_page/index"
      ))
      .then((CityLevelResidentialForSaleListingSearchPage) => {
        CityLevelResidentialForSaleListingSearchPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    // region special_categories

    case PageInstance.Enum.city_level_special_categories_for_rent_listing_search_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/special_categories/city_level_special_categories_listing_search_pages/index"
      ))
      .then((SpecialCategoriesCityListingSearchPages) => {
        SpecialCategoriesCityListingSearchPages?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.city_level_special_categories_for_sale_listing_search_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/special_categories/city_level_special_categories_listing_search_pages/index"
      ))
      .then((SpecialCategoriesCityListingSearchPages) => {
        SpecialCategoriesCityListingSearchPages?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.city_level_special_categories_landing_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/special_categories/city_level_special_categories_landing_page/index"
      ))
      .then((Page) => {
        Page?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    // endregion special_categories

    // region village_houses

    case PageInstance.Enum.city_level_village_houses_for_rent_listing_search_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/special_categories/city_level_village_houses_for_rent_listing_search_page/index"
      ))
      .then((Page) => {
        Page?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.city_level_village_houses_for_sale_listing_search_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/special_categories/city_level_village_houses_for_sale_listing_search_page/index"
      ))
      .then((Page) => {
        Page?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    // endregion village_houses

    // region home_ownership_scheme

    case PageInstance.Enum.city_level_home_ownership_scheme_landing_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/special_categories/city_level_home_ownership_scheme_landing_page/index"
      ))
      .then((Page) => {
        Page?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.neighbourhood_level_home_ownership_scheme_summary_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/special_categories/neighbourhood_level_home_ownership_scheme_summary_page/index"
      ))
      .then((Page) => {
        Page?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.neighbourhood_level_home_ownership_scheme_for_rent_listing_search_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/special_categories/neighbourhood_level_special_categories_listing_search_pages/index"
      ))
      .then((SpecialCategoriesNeighbourhoodListingSearchPages) => {
        SpecialCategoriesNeighbourhoodListingSearchPages?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.neighbourhood_level_home_ownership_scheme_for_sale_listing_search_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/special_categories/neighbourhood_level_special_categories_listing_search_pages/index"
      ))
      .then((SpecialCategoriesNeighbourhoodListingSearchPages) => {
        SpecialCategoriesNeighbourhoodListingSearchPages?.default?.init()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    // endregion home_ownership_scheme

    case PageInstance.Enum.single_residential_neighbourhood_summary_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/neighbourhoods/single_residential_neighbourhood_summary_page/index"
      ))
      .then((SingleResidentialNeighbourhoodSummaryPage) => {
        SingleResidentialNeighbourhoodSummaryPage?.default?.init()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.neighbourhood_level_residential_for_rent_listing_search_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/neighbourhoods/neighbourhood_level_residential_for_rent_listing_search_page/index"
      ))
      .then((NeighbourhoodLevelResidentialForRentListingSearchPage) => {
        NeighbourhoodLevelResidentialForRentListingSearchPage?.default?.init()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.neighbourhood_level_residential_for_sale_listing_search_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/neighbourhoods/neighbourhood_level_residential_for_sale_listing_search_page/index"
      ))
      .then((NeighbourhoodLevelResidentialForSaleListingSearchPage) => {
        NeighbourhoodLevelResidentialForSaleListingSearchPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.neighbourhood_level_residential_transaction_list_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/real_estate_transactions/neighbourhood_level_real_estate_transaction_list_page/index"
      ))
      .then((NeighbourhoodLevelResidentialTransactionListPage) => {
        NeighbourhoodLevelResidentialTransactionListPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.neighbourhood_level_expired_residential_for_rent_listing_list_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/neighbourhoods/neighbourhood_level_expired_residential_for_rent_listing_list_page/index"
      ))
      .then((page) => {
        page?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.neighbourhood_level_expired_residential_for_sale_listing_list_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/neighbourhoods/neighbourhood_level_expired_residential_for_sale_listing_list_page/index"
      ))
      .then((page) => {
        page?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }


    case PageInstance.Enum.single_residential_building_summary_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/buildings/single_residential_building_summary_page/index"
      ))
      .then((SingleResidentialBuildingSummaryPage) => {
        SingleResidentialBuildingSummaryPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.building_level_residential_for_rent_listing_search_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/buildings/building_level_residential_for_rent_listing_search_page/index"
      ))
      .then((BuildingLevelResidentialForRentListingSearchPage) => {
        BuildingLevelResidentialForRentListingSearchPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.building_level_residential_for_sale_listing_search_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/buildings/building_level_residential_for_sale_listing_search_page/index"
      ))
      .then((BuildingLevelResidentialForSaleListingSearchPage) => {
        BuildingLevelResidentialForSaleListingSearchPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.building_level_residential_transaction_list_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/real_estate_transactions/building_level_real_estate_transaction_list_page/index"
      ))
      .then((BuildingLevelResidentialTransactionListPage) => {
        BuildingLevelResidentialTransactionListPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }


    case PageInstance.Enum.building_level_expired_residential_for_rent_listing_list_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/buildings/building_level_expired_residential_for_rent_listing_list_page/index"
      ))
      .then((page) => {
        page?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.building_level_expired_residential_for_sale_listing_list_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/buildings/building_level_expired_residential_for_sale_listing_list_page/index"
      ))
      .then((page) => {
        page?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }


    case PageInstance.Enum.create_one_residential_listing_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/listings/create_one_residential_listing_page/index"
      ))
      .then((CreateOneResidentialListingPage) => {
        CreateOneResidentialListingPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.create_one_commercial_listing_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/listings/create_one_commercial_listing_page/index"
      ))
      .then((CreateOneCommercialListingPage) => {
        CreateOneCommercialListingPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.edit_summary_of_one_residential_listing_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/listings/edit_summary_of_one_residential_listing_page/index"
      ))
      .then((EditSummaryOfOneResidentialListingPage) => {
        EditSummaryOfOneResidentialListingPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.edit_summary_of_one_commercial_listing_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/listings/edit_summary_of_one_commercial_listing_page/index"
      ))
      .then((EditSummaryOfOneCommercialListingPage) => {
        EditSummaryOfOneCommercialListingPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.edit_details_of_one_residential_listing_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/listings/edit_details_of_one_residential_listing_page/index"
      ))
      .then((EditDetailsOfOneResidentialListingPage) => {
        EditDetailsOfOneResidentialListingPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.edit_details_of_one_commercial_listing_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/listings/edit_details_of_one_commercial_listing_page/index"
      ))
      .then((EditDetailsOfOneCommercialListingPage) => {
        EditDetailsOfOneCommercialListingPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.one_residential_listing_preview_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/listings/one_residential_listing_preview_page/index"
      ))
      .then((OneResidentialListingPreviewPage) => {
        OneResidentialListingPreviewPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.one_commercial_listing_preview_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/listings/one_commercial_listing_preview_page/index"
      ))
      .then((OneCommercialListingPreviewPage) => {
        OneCommercialListingPreviewPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.residential_listing_processing_result_for_published_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/listings/residential_listing_processing_result_for_published_page/index"
      ))
      .then((ResidentialListingProcessingResultForPublishedPage) => {
        ResidentialListingProcessingResultForPublishedPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.commercial_listing_processing_result_for_published_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/listings/commercial_listing_processing_result_for_published_page/index"
      ))
      .then((CommercialListingProcessingResultForPublishedPage) => {
        CommercialListingProcessingResultForPublishedPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.residential_listing_processing_result_for_updated_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/listings/residential_listing_processing_result_for_updated_page/index"
      ))
      .then((ResidentialListingProcessingResultForUpdatedPage) => {
        ResidentialListingProcessingResultForUpdatedPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.commercial_listing_processing_result_for_updated_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/listings/commercial_listing_processing_result_for_updated_page/index"
      ))
      .then((CommercialListingProcessingResultForUpdatedPage) => {
        CommercialListingProcessingResultForUpdatedPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.one_residential_listing_payment_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/listings/one_residential_listing_payment_page/index"
      ))
      .then((OneResidentialListingPaymentPage) => {
        OneResidentialListingPaymentPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.one_commercial_listing_payment_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/listings/one_commercial_listing_payment_page/index"
      ))
      .then((OneCommercialListingPaymentPage) => {
        OneCommercialListingPaymentPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.single_listing_details_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/listings/single_listing_details_page/index"
      ))
      .then((SingleListingDetailsPage) => {
        SingleListingDetailsPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }


    case PageInstance.Enum.single_school_summary_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/schools/single_school_summary_page"
      ))
      .then((SingleSchoolSummaryPage) => {
        SingleSchoolSummaryPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.school_for_rent_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/schools/single_school_rental_listing_search_page"
      ))
      .then((Schools_Listings_Rental) => {
        Schools_Listings_Rental?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.school_for_sale_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/schools/single_school_sale_listing_search_page"
      ))
      .then((Schools_Listings_Sale) => {
        Schools_Listings_Sale?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.single_school_expired_for_rent_listing_list_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/schools/single_school_expired_for_rent_listing_list_page/index"
      ))
      .then((Page) => {
        Page?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.single_school_expired_for_sale_listing_list_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/schools/single_school_expired_for_sale_listing_list_page/index"
      ))
      .then((Page) => {
        Page?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }


    case PageInstance.Enum.city_level_public_transport_stop_list_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/public_transport_stops/city_level_public_transport_stop_list_page/index"
      ))
      .then((CityLevelPublicTransportStopListPage) => {
        CityLevelPublicTransportStopListPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.single_public_transport_stop_summary_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/public_transport_stops/single_public_transport_stop_summary_page/index"
      ))
      .then((SinglePublicTransportStopSummaryPage) => {
        SinglePublicTransportStopSummaryPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.public_transport_for_rent_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/public_transport_stops/single_public_transport_stop_rental_listing_search_page/index"
      ))
      .then((PublicTransport_Listings_Rental) => {
        PublicTransport_Listings_Rental?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.public_transport_for_sale_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/public_transport_stops/single_public_transport_stop_sale_listing_search_page/index"
      ))
      .then((PublicTransport_Listings_Sale) => {
        PublicTransport_Listings_Sale?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.single_public_transport_stop_expired_for_rent_listing_list_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/public_transport_stops/single_public_transport_stop_expired_for_rent_listing_list_page/index"
      ))
      .then((PublicTransport_Listings_Sale) => {
        PublicTransport_Listings_Sale?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.single_public_transport_stop_expired_for_sale_listing_list_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/public_transport_stops/single_public_transport_stop_expired_for_sale_listing_list_page/index"
      ))
      .then((PublicTransport_Listings_Sale) => {
        PublicTransport_Listings_Sale?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }


    case PageInstance.Enum.market_news_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "pages/market_news/index"
      ))
      .then((MarketNews) => {
        MarketNews?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.city_level_tragic_event_list_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/tragic_events/city_level_tragic_event_list_page/index"
      ))
      .then((CityLevelTragicEventListPage) => {
        CityLevelTragicEventListPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.house_buying_guide:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "pages/cities/resources/house_buying_guide/index"
      ))
      .then((HouseBuyingGuidePage) => {
        HouseBuyingGuidePage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.city_level_school_list_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/schools/city_level_school_list_page/index"
      ))
      .then((CityLevelSchoolListPage) => {
        CityLevelSchoolListPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.user_signin_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "pages/users/sessions/new"
      ))
      .then((Users_Sessions_New) => {
        Users_Sessions_New?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.user_signup_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "pages/users/registrations/new"
      ))
      .then((Users_Registrations_New) => {
        Users_Registrations_New?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.user_edit_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "pages/users/registrations/edit"
      ))
      .then((Users_Registrations_New) => {
        Users_Registrations_New?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.user_new_password_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "pages/users/passwords/new"
      ))
      .then((Users_Passwords_New) => {
        Users_Passwords_New?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.lister_business_type_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/listers/business_type_pages/all_pages/index"
      ))
      .then((ListerRolePage) => {
        ListerRolePage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.lister_remarketing_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/listers/remarketing_page/index"
      ))
      .then((ListerRemarketingPage) => {
        ListerRemarketingPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.listings_submission_pricing:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "pages/listings/submission/pricing/index"
      ))
      .then((Listings_Submission_Pricing) => {
        Listings_Submission_Pricing?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.city_level_overseas_properties_landing_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/buildings/city_level_overseas_properties_landing_page/index"
      ))
      .then((CityLevelOverseasPropertiesLandingPage) => {
        CityLevelOverseasPropertiesLandingPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.country_level_overseas_marketing_projects_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/buildings/country_level_overseas_marketing_projects_page/index"
      ))
      .then((CountryLevelOverseasMarketingProjectsPage) => {
        CountryLevelOverseasMarketingProjectsPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.city_level_overseas_marketing_projects_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/buildings/city_level_overseas_marketing_projects_page/index"
      ))
      .then((CityLevelOverseasMarketingProjectsPage) => {
        CityLevelOverseasMarketingProjectsPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }


    case PageInstance.Enum.single_street_summary_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/streets/single_street_summary_page/index"
      ))
      .then((SingleStreetSummaryPage) => {
        SingleStreetSummaryPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.single_street_sale_listing_search_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/streets/single_street_sale_listing_search_page/index"
      ))
      .then((SingleStreetSaleListingSearchPage) => {
        SingleStreetSaleListingSearchPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.single_street_rental_listing_search_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/streets/single_street_rental_listing_search_page/index"
      ))
      .then((SingleStreetRentalListingSearchPage) => {
        SingleStreetRentalListingSearchPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.single_street_expired_for_rent_listing_list_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/streets/single_street_expired_for_rent_listing_list_page/index"
      ))
      .then((Page) => {
        Page?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.single_street_expired_for_sale_listing_list_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/streets/single_street_expired_for_sale_listing_list_page/index"
      ))
      .then((Page) => {
        Page?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }


    case PageInstance.Enum.single_landmark_summary_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/landmarks/single_landmark_summary_page/index"
      ))
      .then((SingleLandmarkSummaryPage) => {
        SingleLandmarkSummaryPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.single_landmark_sale_listing_search_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/landmarks/single_landmark_sale_listing_search_page/index"
      ))
      .then((SingleLandmarkSaleListingSearchPage) => {
        SingleLandmarkSaleListingSearchPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.single_landmark_rental_listing_search_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/landmarks/single_landmark_rental_listing_search_page/index"
      ))
      .then((SingleLandmarkRentalListingSearchPage) => {
        SingleLandmarkRentalListingSearchPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.single_landmark_expired_for_rent_listing_list_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/landmarks/single_landmark_expired_for_rent_listing_list_page/index"
      ))
      .then((page) => {
        page?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.single_landmark_expired_for_sale_listing_list_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/landmarks/single_landmark_expired_for_sale_listing_list_page/index"
      ))
      .then((page) => {
        page?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }


    case PageInstance.Enum.single_real_estate_agency_summary_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/real_estate_agencies/single_real_estate_agency_summary_page"
      ))
      .then((SingleRealEstateAgencySummaryPage) => {
        SingleRealEstateAgencySummaryPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }


    case PageInstance.Enum.single_real_estate_agency_rental_listing_search_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/real_estate_agencies/single_real_estate_agency_rental_listing_search_page"
      ))
      .then((SingleRealEstateAgencyRentalListingSearchPage) => {
        SingleRealEstateAgencyRentalListingSearchPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }


    case PageInstance.Enum.single_real_estate_agency_sale_listing_search_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/real_estate_agencies/single_real_estate_agency_sale_listing_search_page"
      ))
      .then((SingleRealEstateAgencySaleListingSearchPage) => {
        SingleRealEstateAgencySaleListingSearchPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }


    case PageInstance.Enum.city_level_new_development_projects_landing_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/new_property_developments/city_level_new_development_projects_landing_page/index"
      ))
      .then((CityLevelNewDevelopmentProjectsLandingPage) => {
        CityLevelNewDevelopmentProjectsLandingPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.neighbourhood_level_new_development_projects_summary_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/new_property_developments/neighbourhood_level_new_development_projects_summary_page/index"
      ))
      .then((NeighbourhoodLevelNewDevelopmentProjectsSummaryPage) => {
        NeighbourhoodLevelNewDevelopmentProjectsSummaryPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.one_new_development_project_summary_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/buildings/one_new_development_project_summary_page/index"
      ))
      .then((OneNewDevelopmentProjectSummaryPage) => {
        OneNewDevelopmentProjectSummaryPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.new_development_project_level_residential_for_rent_listing_search_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/buildings/new_development_project_level_residential_for_rent_listing_search_page/index"
      ))
      .then((NewDevelopmentProjectLevelResidentialForRentListingSearchPage) => {
        NewDevelopmentProjectLevelResidentialForRentListingSearchPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.new_development_project_level_residential_for_sale_listing_search_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/buildings/new_development_project_level_residential_for_sale_listing_search_page/index"
      ))
      .then((NewDevelopmentProjectLevelResidentialForSaleListingSearchPage) => {
        NewDevelopmentProjectLevelResidentialForSaleListingSearchPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.city_level_serviced_apartments_landing_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/serviced_apartments/city_level_serviced_apartments_landing_page/index"
      ))
      .then((CityLevelServicedApartmentsLandingPage) => {
        CityLevelServicedApartmentsLandingPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.neighbourhood_level_serviced_apartment_list_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/serviced_apartments/neighbourhood_level_serviced_apartment_list_page/index"
      ))
      .then((NeighbourhoodLevelServicedApartmentListPage) => {
        NeighbourhoodLevelServicedApartmentListPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.city_level_coliving_spaces_landing_page: {
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/coliving_spaces/city_level_coliving_spaces_landing_page/index"
      ))
      .then((page) => {
        page?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.neighbourhood_level_coliving_space_list_page: {
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/coliving_spaces/neighbourhood_level_coliving_space_list_page/index"
      ))
      .then((page) => {
        page?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.single_coliving_space_summary_page: {
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/coliving_spaces/single_coliving_space_summary_page/index"
      ))
      .then((page) => {
        page?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.listings_contact_requests_create_several_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "pages/listings/contact/requests/create_several/index"
      ))
      .then((Listings_Contact_Requests_CreateSeveral) => {
        Listings_Contact_Requests_CreateSeveral?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.one_overseas_project_summary_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/buildings/single_overseas_residential_project_summary_page/index"
      ))
      .then((OneOverseasProjectSummaryPage) => {
        OneOverseasProjectSummaryPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.city_level_estate_building_transaction_ranking_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/real_estate_transactions/city_level_estate_building_transaction_ranking_page/index"
      ))
      .then((CityLevelEstateBuildingTransactionRankingPage) => {
        CityLevelEstateBuildingTransactionRankingPage.default.init()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.city_level_real_estate_transaction_data_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/real_estate_transactions/city_level_real_estate_transaction_data_page/index"
      ))
      .then((CityLevelRealEstateTransactionDataPage) => {
        CityLevelRealEstateTransactionDataPage.default.init()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.city_level_real_estate_transaction_landing_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/real_estate_transactions/city_level_real_estate_transaction_landing_page/index"
      ))
      .then((CityLevelRealEstateTransactionLandingPage) => {
        CityLevelRealEstateTransactionLandingPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.single_serviced_apartment_summary_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/serviced_apartments/single_serviced_apartment_summary_page/index"
      ))
      .then((SingleServicedApartmentSummaryPage) => {
        SingleServicedApartmentSummaryPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.lister_landing_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/listers/lister_landing_page/index"
      ))
      .then((Page) => {
        Page?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.my_news_feed_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "pages/my/news_feeds/index"
      ))
      .then((MyNewsFeedPage) => {
        MyNewsFeedPage?.default?.init?.()
      })
      break
    }

    case PageInstance.Enum.my_follow_management_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/personal/my_follow_management/index"
      ))
      .then((FollowManagementPage) => {
        FollowManagementPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.my_lister_landing_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/personal/my_lister_landing_page/index"
      ))
      .then((MyListerLandingPage) => {
        MyListerLandingPage?.default?.init?.()
      })
      break
    }
    case PageInstance.Enum.my_listings_posted_listings_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/personal/my_listings_posted_listings_page/index"
      ))
      .then((MyListingsPostedListingsPage) => {
        MyListingsPostedListingsPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.my_searches_edit_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/personal/my_searches_edit_page/index"
      ))
      .then((MySearchesEditPage) => {
        MySearchesEditPage?.default?.init?.()
      })
      break
    }

    case PageInstance.Enum.my_notification_subscriptions_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/personal/my_notification_subscriptions_page/index"
      ))
      .then((MyNotificationSubscriptionsPage) => {
        MyNotificationSubscriptionsPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.my_agency_clients_index_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/personal/my_agency_clients/index_page/index"
      ))
      .then((MyAgencyClientsIndexPage) => {
        MyAgencyClientsIndexPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.my_agency_clients_record_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/personal/my_agency_clients/record_page/index"
      ))
      .then((MyAgencyClientsRecordPage) => {
        MyAgencyClientsRecordPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.my_enquiry_history_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/personal/my_enquiry_history_page/index"
      ))
      .then((MyEnquiryHistoryPage) => {
        MyEnquiryHistoryPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.my_expiring_listings_renewal_payment_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/personal/my_expiring_listings_renewal_payment_page/index"
      ))
      .then((MyExpiringListingsRenewalPaymentPage) => {
        MyExpiringListingsRenewalPaymentPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.my_active_listings_upgrade_payment_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/personal/my_active_listings_upgrade_payment_page/index"
      ))
      .then((MyActiveListingsUpgradePaymentPage) => {
        MyActiveListingsUpgradePaymentPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.my_unsubscribe_one_email_confirmation_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/personal/my_unsubscribe_one_email_confirmation_page/index"
      ))
      .then((MyUnsubscribeOneEmailConfirmationPage) => {
        MyUnsubscribeOneEmailConfirmationPage?.default?.create?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.my_shortlisted_listing_list_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/personal/my_shortlisted_listing_list_page/index"
      ))
      .then((MyShortlistedListingListPage) => {
        MyShortlistedListingListPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.create_one_my_building_user_data_alias_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/personal/create_one_my_building_user_data_alias_page/index"
      ))
      .then((CreateOneMyBuildingUserDataAliasPage) => {
        CreateOneMyBuildingUserDataAliasPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.my_unresponded_listing_contact_request_list_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/personal/my_unresponded_listing_contact_request_list_page/index"
      ))
      .then((MyUnrespondedListingContactRequestListPage) => {
        MyUnrespondedListingContactRequestListPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.report_card_my_listing_manager_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/personal/my_listing_manager/report_card/index"
      ))
      .then((Page) => {
        Page?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.my_listing_manager_summary_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/personal/my_listing_manager/summary/index"
      ))
      .then((MyListingManagerSummaryPage) => {
        MyListingManagerSummaryPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.listings_my_listing_manager_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/personal/my_listing_manager/listings/index"
      ))
      .then((MyListingManagerListingsPage) => {
        MyListingManagerListingsPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.users_my_listing_manager_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/personal/my_listing_manager/users/index"
      ))
      .then((MyListingManagerUsersPage) => {
        MyListingManagerUsersPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.clients_my_listing_manager_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/personal/my_listing_manager/clients/index"
      ))
      .then((MyListingManagerClientsPage) => {
        MyListingManagerClientsPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.one_record_clients_my_listing_manager_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/personal/my_listing_manager/clients/record"
      ))
      .then((MyListingManagerClientRecordPage) => {
        MyListingManagerClientRecordPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.listings_landlord_lister_dashboard_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/personal/landlord_lister_dashboard/listings/index"
      ))
      .then((LandlordListerDashboardListingsPage) => {
        LandlordListerDashboardListingsPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.new_development_project_agency_section_summary_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/personal/new_development_project_agency_section_summary_page/index"
        ))
        .then((NewDevelopmentProjectAgencySectionSummaryPage) => {
          NewDevelopmentProjectAgencySectionSummaryPage?.default?.init?.()
        })
        .catch((error) => {
          captureException(error)
        })
      break
    }

    case PageInstance.Enum.real_estate_developer_lister_dashboard__listing_management_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/personal/real_estate_developer_lister_dashboard/listing_management_page/index"
      ))
      .then((RealEstateDeveloperListerDashboardListingsPage) => {
        RealEstateDeveloperListerDashboardListingsPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.city_level_create_one_valuation_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/property_valuation/city_level_create_one_valuation_page/index"
      ))
      .then((CityLevelCreateOneValuationPage) => {
        CityLevelCreateOneValuationPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.city_level_one_valuation_result_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/property_valuation/city_level_one_valuation_result_page/index"
      ))
      .then((CityLevelOneValuationResultPage) => {
        CityLevelOneValuationResultPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.city_level_real_estate_mortgages_buy_rent_summary_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/mortgages/city_level_real_estate_mortgages_buy_rent_summary_page/index"
      ))
      .then((CityLevelRealEstateMortgagesBuyRentSummaryPage) => {
        CityLevelRealEstateMortgagesBuyRentSummaryPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.city_level_real_estate_mortgage_calculator_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/mortgages/city_level_real_estate_mortgage_calculator_page/index"
      ))
      .then((CityLevelRealEstateMortgageCalculatorPage) => {
        CityLevelRealEstateMortgageCalculatorPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }


    case PageInstance.Enum.city_level_commercial_rental_onboarding_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/cities/city_level_commercial_rental_onboarding_page/index"
      ))
      .then((CityLevelCommercialRentalOnboardingPage) => {
        CityLevelCommercialRentalOnboardingPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.city_level_commercial_sale_onboarding_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/cities/city_level_commercial_sale_onboarding_page/index"
      ))
      .then((CityLevelCommercialSaleOnboardingPage) => {
        CityLevelCommercialSaleOnboardingPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.city_level_commercial_for_rent_listing_search_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/cities/city_level_commercial_for_rent_listing_search_page/index"
      ))
      .then((Page) => {
        Page?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.city_level_commercial_for_sale_listing_search_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/cities/city_level_commercial_for_sale_listing_search_page/index"
      ))
      .then((Page) => {
        Page?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.city_level_coworking_space_landing_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/business_centers/city_level_coworking_space_landing_page/index"
      ))
      .then((CityLevelCoworkingSpaceLandingPage) => {
        CityLevelCoworkingSpaceLandingPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.city_level_business_center_landing_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/business_centers/city_level_business_center_landing_page/index"
      ))
      .then((CityLevelBusinessCenterLandingPage) => {
        CityLevelBusinessCenterLandingPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.city_level_serviced_office_landing_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/business_centers/city_level_serviced_office_landing_page/index"
      ))
      .then((CityLevelServicedOfficeLandingPage) => {
        CityLevelServicedOfficeLandingPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.single_business_center_details_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/business_centers/single_business_center_details_page/index"
      ))
      .then((SingleBusinessCenterDetailsPage) => {
        SingleBusinessCenterDetailsPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.single_serviced_office_details_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/business_centers/single_serviced_office_details_page/index"
      ))
      .then((Page) => {
        Page?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.single_coworking_space_details_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/business_centers/single_coworking_space_details_page/index"
      ))
      .then((Page) => {
        Page?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.city_level_commercial_landing_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/top_level/city_level_commercial_landing_page/index"
      ))
      .then((CityLevelCommercialLandingPage) => {
        CityLevelCommercialLandingPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.city_level_commercial_neighbourhood_list_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/cities/city_level_commercial_neighbourhood_list_page/index"
      ))
      .then((CityLevelCommercialNeighbourhoodListPage) => {
        CityLevelCommercialNeighbourhoodListPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }


    case PageInstance.Enum.single_commercial_neighbourhood_summary_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/neighbourhoods/single_commercial_neighbourhood_summary_page/index"
      ))
      .then((SingleCommercialNeighbourhoodSummaryPage) => {
        SingleCommercialNeighbourhoodSummaryPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.neighbourhood_level_commercial_for_rent_listing_search_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/neighbourhoods/neighbourhood_level_commercial_for_rent_listing_search_page/index"
      ))
      .then((NeighbourhoodLevelCommercialForRentListingSearchPage) => {
        NeighbourhoodLevelCommercialForRentListingSearchPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.neighbourhood_level_commercial_for_sale_listing_search_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/neighbourhoods/neighbourhood_level_commercial_for_sale_listing_search_page/index"
      ))
      .then((NeighbourhoodLevelCommercialForSaleListingSearchPage) => {
        NeighbourhoodLevelCommercialForSaleListingSearchPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.neighbourhood_level_expired_commercial_for_rent_listing_list_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/neighbourhoods/neighbourhood_level_expired_commercial_for_rent_listing_list_page/index"
      ))
      .then((Page) => {
        Page?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.neighbourhood_level_expired_commercial_for_sale_listing_list_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/neighbourhoods/neighbourhood_level_expired_commercial_for_sale_listing_list_page/index"
      ))
      .then((Page) => {
        Page?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }


    case PageInstance.Enum.single_commercial_building_summary_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/buildings/single_commercial_building_summary_page/index"
      ))
      .then((SingleCommercialBuildingSummaryPage) => {
        SingleCommercialBuildingSummaryPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.building_level_commercial_for_rent_listing_search_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/buildings/building_level_commercial_for_rent_listing_search_page/index"
      ))
      .then((BuildingLevelCommercialForRentListingSearchPage) => {
        BuildingLevelCommercialForRentListingSearchPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.building_level_commercial_for_sale_listing_search_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/buildings/building_level_commercial_for_sale_listing_search_page/index"
      ))
      .then((BuildingLevelCommercialForSaleListingSearchPage) => {
        BuildingLevelCommercialForSaleListingSearchPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.building_level_expired_commercial_for_rent_listing_list_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/buildings/building_level_expired_commercial_for_rent_listing_list_page/index"
      ))
      .then((Page) => {
        Page?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.building_level_expired_commercial_for_sale_listing_list_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/buildings/building_level_expired_commercial_for_sale_listing_list_page/index"
      ))
      .then((Page) => {
        Page?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.internal_building_manager_new_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/internal/building_manager/new_one_record/index"
      ))
      .then((InternalBuildingManagerNewPage) => {
        InternalBuildingManagerNewPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.internal_building_manager_edit_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/internal/building_manager/edit_one_record/index"
      ))
      .then((InternalBuildingManagerEditPage) => {
        InternalBuildingManagerEditPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.internal_building_manager_price_list_index_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/internal/building_manager/price_list/index/index"
      ))
      .then((InternalBuildingManagerPriceListIndexPage) => {
        InternalBuildingManagerPriceListIndexPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.internal_resources_spacious_economic_indicator_records_index_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/internal/resources/spacious_economic_indicator_records/index"
        ))
        .then((InternalResourcesSpaciousEconomicIndicatorRecordsIndexPage) => {
          InternalResourcesSpaciousEconomicIndicatorRecordsIndexPage?.default?.init?.()
        })
        .catch((error) => {
          captureException(error)
        })
      break
    }

    case PageInstance.Enum.internal_crm_index_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/internal/crm/shared/index"
      ))
      .then((InternalCrmSharedPages) => {
        InternalCrmSharedPages?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.internal_crm_dashboard_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/internal/crm/dashboard/index_page/index"
      ))
      .then((InternalCrmDashboardIndexPage) => {
        InternalCrmDashboardIndexPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.internal_crm_leads_index_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/internal/crm/leads/index_page/index"
      ))
      .then((InternalCrmLeadsIndexPage) => {
        InternalCrmLeadsIndexPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.internal_crm_leads_record_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/internal/crm/shared/index"
      ))
      .then((InternalCrmSharedPages) => {
        InternalCrmSharedPages?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.internal_crm_prospects_index_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/internal/crm/prospects/index_page/index"
      ))
      .then((InternalCrmProspectsIndexPage) => {
        InternalCrmProspectsIndexPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.internal_crm_single_prospect_record_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/internal/crm/single_prospect_details_page/index"
      ))
      .then((page) => {
        page?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.internal_crm_closed_deals_index_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/internal/crm/closed_deals/index_page/index"
      ))
      .then((InternalCrmClosedDealsIndexPage) => {
        InternalCrmClosedDealsIndexPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.internal_crm_featured_listing_quotas_index_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/internal/crm/featured_listing_quotas/index_page/index"
      ))
      .then((InternalCrmClosedDealsIndexPage) => {
        InternalCrmClosedDealsIndexPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.internal_crm_client_services_index_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/internal/crm/client_services/index_page/index"
      ))
      .then((InternalCrmClientServicesIndexPage) => {
        InternalCrmClientServicesIndexPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.internal_crm_client_services_record_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/internal/crm/shared/index"
      ))
      .then((InternalCrmSharedPages) => {
        InternalCrmSharedPages?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.internal_crm_analytics_sales_performance_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/internal/crm/analytics/sales_performance/index"
      ))
      .then((InternalCrmAnalyticsSalesPerformancePage) => {
        InternalCrmAnalyticsSalesPerformancePage?.default?.create?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.internal_crm_analytics_lead_created_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/internal/crm/analytics/lead_created/index"
      ))
      .then((InternalCrmAnalyticsLeadCreatedPage) => {
        InternalCrmAnalyticsLeadCreatedPage?.default?.create?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.internal_crm_analytics_lead_quality:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/internal/crm/analytics/lead_quality/index"
      ))
      .then((InternalCrmAnalyticsLeadQualityPage) => {
        InternalCrmAnalyticsLeadQualityPage?.default?.create?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.internal_crm_analytics_lead_assigned_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/internal/crm/analytics/lead_assigned/index"
      ))
      .then((InternalCrmAnalyticsLeadAssignedPage) => {
        InternalCrmAnalyticsLeadAssignedPage?.default?.create?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.internal_crm_analytics_lead_internal_source_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/internal/crm/analytics/lead_internal_source/index"
      ))
      .then((InternalCrmAnalyticsLeadInternalSourcePage) => {
        InternalCrmAnalyticsLeadInternalSourcePage?.default?.create?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.internal_crm_analytics_calls_logged_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/internal/crm/analytics/calls_logged/index"
      ))
      .then((InternalCrmAnalyticsCallsLoggedPage) => {
        InternalCrmAnalyticsCallsLoggedPage?.default?.create?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.internal_crm_analytics_prospecting_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/internal/crm/analytics/prospecting/index"
      ))
      .then((InternalCrmAnalyticsProspectingPage) => {
        InternalCrmAnalyticsProspectingPage?.default?.create?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.internal_crm_analytics_lead_rejection_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/internal/crm/analytics/lead_rejection/index"
      ))
      .then((InternalCrmAnalyticsLeadRejectionPage) => {
        InternalCrmAnalyticsLeadRejectionPage?.default?.create?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.internal_crm_analytics_renewal_prospects_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/internal/crm/analytics/renewal_prospects/index"
      ))
      .then((InternalCrmAnalyticsRenewalProspectsPage) => {
        InternalCrmAnalyticsRenewalProspectsPage?.default?.create?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    // region prospect_invoices

    case PageInstance.Enum.internal__crm__prospect_invoices__edit_one_prospect_invoice_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/internal/crm/prospect_invoices/edit_one_prospect_invoice_page/index"
      ))
      .then((module) => {
        module?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    // endregion prospect_invoices



    // region real estate transaction page

    case PageInstance.Enum.page__internal_real_estate_transaction__gov_transaction_records_audit_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/internal/real_estate_transaction/gov_transaction_records_audit_page/index"
      ))
      .then((InternalRealEstateTransactionImportNewTransactionRecordsPage) => {
        InternalRealEstateTransactionImportNewTransactionRecordsPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.page__internal_real_estate_transaction__import_new_transaction_records_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/internal/real_estate_transaction/import_new_transaction_records_page/index"
      ))
      .then((InternalRealEstateTransactionImportNewTransactionRecordsPage) => {
        InternalRealEstateTransactionImportNewTransactionRecordsPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    // endregion real estate transaction page


    // region real estate transaction page

    case PageInstance.Enum.page__internal_forms_neighbourhood_summary_content_update_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/internal/forms/neighbourhood_summary_content_update_page/index"
      ))
      .then((InternalFormsNeighbourhoodSummaryContentUpdatePage) => {
        InternalFormsNeighbourhoodSummaryContentUpdatePage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    // endregion real estate transaction page


    case PageInstance.Enum.internal_report_card_agencies_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/internal/report_card/agencies/index_page/index"
      ))
      .then((InternalReportCardAgenciesIndexPage) => {
        InternalReportCardAgenciesIndexPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.internal_report_card_agencies_record_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/internal/report_card/agencies/record_page/index"
      ))
      .then((InternalReportCardAgenciesRecordPage) => {
        InternalReportCardAgenciesRecordPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.internal_users_user_agency_relationship_management_request_one_user_belonging_agency_update_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/internal/users/user_agency_relationship_management/request_one_user_belonging_agency_update_page/index"
      ))
      .then((InternalUsersUserAgencyRelationshipManagementRequestOneUserBelongingAgencyUpdatePage) => {
        InternalUsersUserAgencyRelationshipManagementRequestOneUserBelongingAgencyUpdatePage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.internal_users_user_agency_relationship_management_request_one_user_managing_agencies_update_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/internal/users/user_agency_relationship_management/request_one_user_managing_agencies_update_page/index"
      ))
      .then((InternalUsersUserAgencyRelationshipManagementRequestOneUserManagingAgenciesUpdatePage) => {
        InternalUsersUserAgencyRelationshipManagementRequestOneUserManagingAgenciesUpdatePage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.internal_sales_report_listings_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/internal/sales_report/listings/index_page/index"
      ))
      .then((InternalSalesReportListingsIndexPage) => {
        InternalSalesReportListingsIndexPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.internal_sales_report_listings_record_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/internal/sales_report/listings/record_page/index"
      ))
      .then((InternalSalesReportListingsRecordPage) => {
        InternalSalesReportListingsRecordPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.internal_sales_report_listings_summary_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/internal/sales_report/listings/summary_page/index"
      ))
      .then((InternalSalesReportListingsSummaryPage) => {
        InternalSalesReportListingsSummaryPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.internal_sales_report_listing_contact_requests_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/internal/sales_report/listing_contact_requests/index"
      ))
      .then((InternalSalesReportListingContactRequestsPage) => {
        InternalSalesReportListingContactRequestsPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.internal_sales_report_neighbourhood_level_email_enquiry_report_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/internal/sales_report/listing_contact_requests_nhood/index"
      ))
      .then((InternalSalesReportNeighbourhoodLevelEmailEnquiryReportPage) => {
        InternalSalesReportNeighbourhoodLevelEmailEnquiryReportPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.internal_sales_report_listing_searches_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/internal/sales_report/listing_searches/index"
      ))
      .then((InternalSalesReportListingSearchesPage) => {
        InternalSalesReportListingSearchesPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.internal_sales_report_business_centers_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/internal/sales_report/business_centers/index"
      ))
      .then((InternalSalesReportBusinessCentersPage) => {
        InternalSalesReportBusinessCentersPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.internal_sales_report_serviced_apartment_providers_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/internal/sales_report/serviced_apartment_providers/index_page/index"
      ))
      .then((InternalSalesReportServicedApartmentProvidersIndexPage) => {
        InternalSalesReportServicedApartmentProvidersIndexPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.internal_sales_report_coliving_spaces_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/internal/sales_report/coliving_spaces/index_page/index"
      ))
      .then((InternalSalesReportColivingSpacesIndexPage) => {
        InternalSalesReportColivingSpacesIndexPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.internal_sales_report_featured_agency_banners_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/internal/sales_report/featured_agency_banners/index"
      ))
      .then((InternalSalesReportFeaturedAgencyBannersIndexPage) => {
        InternalSalesReportFeaturedAgencyBannersIndexPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.internal_sales_report_overseas_properties_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/internal/sales_report/overseas_properties/index"
      ))
      .then((InternalSalesReportOverseasPropertiesIndexPage) => {
        InternalSalesReportOverseasPropertiesIndexPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.internal_sales_report_agencies_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/internal/sales_report/agencies/index_page/index"
      ))
      .then((InternalSalesReportAgenciesIndexPage) => {
        InternalSalesReportAgenciesIndexPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.internal_sales_report_agencies_record_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/internal/sales_report/agencies/record_page/index"
      ))
      .then((InternalSalesReportAgenciesRecordPage) => {
        InternalSalesReportAgenciesRecordPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.internal_sales_report_agents_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/internal/sales_report/agents/index_page/index"
      ))
      .then((InternalSalesReportAgentsIndexPage) => {
        InternalSalesReportAgentsIndexPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }
    case PageInstance.Enum.internal_sales_report_agents_record_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/internal/sales_report/agents/record_page/index"
      ))
      .then((InternalSalesReportAgentsRecordPage) => {
        InternalSalesReportAgentsRecordPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.internal_buildings_resource_list_page_desktop_ver:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/internal/internal/resources/buildings/resource_list_page/desktop_ver"
      ))
      .then((InternalBuildingsResourceListPageDesktopVer) => {
        InternalBuildingsResourceListPageDesktopVer?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.lister_dashboard_2020__one_listing_data_feed_processing_job_rejected_listings_summary_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/lister_dashboard_2020/listing_remote_data_feed_jobs/one_listing_data_feed_processing_job_rejected_listings_summary_page/index"
      ))
      .then((Page) => {
        Page?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.lister_dashboard_2020__create_one_real_estate_agency_custom_building_mapping_record_from_one_listing_remote_data_feed_entry_processing_record_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/lister_dashboard_2020/listing_remote_data_feed_jobs/create_one_real_estate_agency_custom_building_mapping_record_from_one_listing_remote_data_feed_entry_processing_record_page/index"
      ))
      .then((Page) => {
        Page?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.internal_agency_custom_building_mappings_new_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/internal/real_estate_agency_custom_building_mappings/create_new_record_page/index"
      ))
      .then((InternalAgencyCustomBuildingMappingsNewPage) => {
        InternalAgencyCustomBuildingMappingsNewPage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.mobile_app_redirect_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/redirects/mobile_app_redirect_page/index"
      ))
      .then((MobileAppRedirectPage) => {
        MobileAppRedirectPage?.default?.create?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    case PageInstance.Enum.universal_resource_not_found_message_page:{
      page_load_promise = dynamicImportWithRetry(() => import(
        "apps/spacious_website/pages/errors/universal_resource_not_found_message_page/index"
      ))
      .then((UniversalResourceNotFoundMessagePage) => {
        UniversalResourceNotFoundMessagePage?.default?.init?.()
      })
      .catch((error) => {
        captureException(error)
      })
      break
    }

    default:
      log.debug("unknown page load")
      break
  }

  readyState.load.then((load_state) => {
    console.debug("readyState (expected `load`): ", load_state)
    requestIdleCallback(() => {
      if (performance == null || typeof performance.getEntriesByType !== "function") {
        // Not supported, skip
        return
      }

      let nav_entry = null
      const current_url_str = window.location.toString()
      performance.getEntriesByType("navigation").forEach((p_entry) => {
        // Skip loop if already found
        if (nav_entry != null) { return }
        // Only look for current document URL (might have prerender of other pages)
        if (p_entry.name !== current_url_str) { return }

        nav_entry = p_entry
      })

      // Not found = not supported = bye
      if (nav_entry == null) { return }

      window.spacious_post_tti_tasks = window.spacious_post_tti_tasks || []
      window.spacious_post_tti_tasks.push(() => {
        // For doc of all options see
        // https://developer.mozilla.org/en-US/docs/Web/API/WindowOrWorkerGlobalScope/fetch
        fetch("/api/web_app/user_performance_data_entries", {
          method:       "POST",
          cache:        "no-cache",
          // Don't think we need credentials for performance data
          credentials:  "omit",
          redirect:     "error",

          // Allow the request to be sent even page closed
          keepalive: true,

          headers: {
            "Content-Type": "application/json",
            // AJAX simulation
            "X-Requested-With": "XMLHttpRequest",
          },

          // body data type must match "Content-Type" header
          body: JSON.stringify({
            performance_entry_type:       nav_entry.entryType,
            performance_entry_name:       nav_entry.name,
            performance_entry_start_time: nav_entry.startTime,
            performance_entry_duration:   nav_entry.duration,

            // Optional attributes from PerformanceNavigationTiming
            // https://developer.mozilla.org/en-US/docs/Web/API/PerformanceNavigationTiming
            // https://www.w3.org/TR/resource-timing-2/
            // https://www.w3.org/TR/navigation-timing-2/
            performance_entry_redirect_start:           nav_entry.redirectStart,
            performance_entry_redirect_end:             nav_entry.redirectEnd,
            performance_entry_fetch_start:              nav_entry.fetchStart,
            performance_entry_domain_lookup_start:      nav_entry.domainLookupStart,
            performance_entry_domain_lookup_end:        nav_entry.domainLookupEnd,
            performance_entry_connect_start:            nav_entry.connectStart,
            performance_entry_connect_end:              nav_entry.connectEnd,
            performance_entry_secure_connection_start:  nav_entry.secureConnectionStart,
            performance_entry_request_start:            nav_entry.requestStart,
            performance_entry_response_start:           nav_entry.responseStart,
            performance_entry_response_end:             nav_entry.responseEnd,
            performance_entry_transfer_size:            nav_entry.transferSize,
            performance_entry_encoded_body_size:        nav_entry.encodedBodySize,
            performance_entry_next_hop_protocol:        nav_entry.nextHopProtocol,

            performance_entry_unload_event_start:             nav_entry.unloadEventStart,
            performance_entry_unload_event_end:               nav_entry.unloadEventEnd,
            performance_entry_dom_interactive:                nav_entry.domInteractive,
            performance_entry_dom_content_loaded_event_start: nav_entry.domContentLoadedEventStart,
            performance_entry_dom_content_loaded_event_end:   nav_entry.domContentLoadedEventEnd,
            performance_entry_dom_complete:                   nav_entry.domComplete,
            performance_entry_load_event_start:               nav_entry.loadEventStart,
            performance_entry_load_event_end:                 nav_entry.loadEventEnd,
            performance_entry_navigation_type:                nav_entry.type,
            performance_entry_redirect_count:                 nav_entry.redirectCount,

            spacious_page_type_identifier: PageInstance.currentPage(),
            spacious_page_html_id:         document.querySelector("html").id,
          }),
        })
        .catch(function(){
          // Do nothing
          // Properly timeout or network issue
        })
      })
    })

    requestIdleCallback(() => {
      readyState.complete.then((complete_state) => {
        console.debug("readyState (expected `complete`): ", complete_state)
        // When TTI is detected, it should be safe to load really not important JS
        dynamicImportWithRetry(() => import(
          "app/deferred_javascript_loader"
        ))
        .then((module) => {
          requestIdleCallback(() => {
            module?.loadAllPostTTIScripts?.()
          })
        })

        dynamicImportWithRetry(() => import(
          "app/deferred_task_executor"
        ))
        .then((module) => {
          // We don't use `requestIdleCallback` here since
          // It's after TTI already
          module?.runAllPostTTITasks?.()
        })
      })
    })

    requestIdleCallback(() => {
      // `instant.page` requires `Set`
      if (typeof Set !== "function") {
        return
      }

      // Make links preloaded with prefetch
      // https://instant.page/
      //
      // Nothing to be called, it's self-executing
      dynamicImportWithRetry(() => import(
        "instant.page"
      ))
      .catch((error) => {
        captureException(error)
      })
    })

    // Load some scripts after page specific JS execution
    return dynamicImportWithRetry(() => import(
      "app/deferred_javascript_loader"
    ))
    .then((module) => {
      requestIdleCallback(() => {
        module?.loadAllPendingScripts?.()
      })
    })
    .catch((error) => {
      captureException(error)
    })
  })
  .catch((error) => {
    captureException(error)
  })

  return page_load_promise
}

export default start
